/**
 * Tier-based permission matrix
 * Defines which follower permissions are allowed for each subscription tier
 */

import type { SubscriptionTier } from './subscriptionTier';

// Follower permission keys from the database
export type FollowerPermissionKey =
  | 'showRecentBets'
  | 'showActivityFeed'
  | 'showDetailedAnalysis'
  | 'allowTailParlays'
  | 'showPropBreakdown'
  | 'showFinancialDetails';

/** Full follower permissions object shape (used by settings UI) */
export type FollowerPermissions = Record<FollowerPermissionKey, boolean>;

/** Default follower permissions (all disabled) */
export const DEFAULT_FOLLOWER_PERMISSIONS: FollowerPermissions = {
  showDetailedAnalysis: false,
  allowTailParlays: false,
  showPropBreakdown: false,
  showRecentBets: false,
  showActivityFeed: false,
  showFinancialDetails: false,
};

/**
 * Permission matrix: which permissions each tier gets
 *
 * - Free: No permissions (free users cannot be followed)
 * - Pro: All social permissions including financial transparency
 * - Premium: Same social permissions as Pro (differentiation is in themes + analysis modes)
 * - Admin: All permissions (same as Premium)
 */
export const TIER_PERMISSIONS: Record<SubscriptionTier, Set<FollowerPermissionKey>> = {
  free: new Set([]), // Free users can't be followed, so no permissions needed
  pro: new Set([
    'showRecentBets',
    'showActivityFeed',
    'showDetailedAnalysis',
    'allowTailParlays',
    'showPropBreakdown',
    'showFinancialDetails',
  ]),
  premium: new Set([
    'showRecentBets',
    'showActivityFeed',
    'showDetailedAnalysis',
    'allowTailParlays',
    'showPropBreakdown',
    'showFinancialDetails',
  ]),
  elite: new Set([
    'showRecentBets',
    'showActivityFeed',
    'showDetailedAnalysis',
    'allowTailParlays',
    'showPropBreakdown',
    'showFinancialDetails',
  ]),
  admin: new Set([
    'showRecentBets',
    'showActivityFeed',
    'showDetailedAnalysis',
    'allowTailParlays',
    'showPropBreakdown',
    'showFinancialDetails',
  ]),
};

/**
 * Check if a specific permission is allowed for a given tier
 */
export function isPermissionAllowed(
  tier: SubscriptionTier,
  permission: FollowerPermissionKey,
): boolean {
  return TIER_PERMISSIONS[tier]?.has(permission) ?? false;
}

/**
 * All tier-gated features/settings that should reset on downgrade
 *
 * - Premium: Custom themes, detailed analysis mode
 * - Pro: All follower permissions, social share, best line finder
 */
export const TIER_FEATURES = {
  premium: ['customThemes', 'analysisDetailed'],
  pro: ['followerPermissions', 'socialShare', 'bestLineFinder', 'showFinancialDetails'],
} as const;

/**
 * Get all allowed permissions for a tier
 */
export function getAllowedPermissions(tier: SubscriptionTier): FollowerPermissionKey[] {
  return Array.from(TIER_PERMISSIONS[tier] || []);
}

// =============================================================================
// Friend Permissions (same keys, different defaults, no tier-gating)
// =============================================================================

/** Same keys as follower permissions but for friends */
export type FriendPermissionKey = FollowerPermissionKey;

/** Default friend permissions — mostly ON since friends are trusted */
export const DEFAULT_FRIEND_PERMISSIONS: Record<FriendPermissionKey, boolean> = {
  showDetailedAnalysis: true,
  allowTailParlays: true,
  showPropBreakdown: true,
  showRecentBets: true,
  showActivityFeed: true,
  showFinancialDetails: false,
};

// =============================================================================
// Shared Permission Config (drives settings UI on web and mobile)
// =============================================================================

export const PERMISSION_CONFIG: ReadonlyArray<{
  key: FollowerPermissionKey;
  label: string;
  description: string;
  /** If true, auto-disabled when showRecentBets is false */
  dependsOnRecentBets: boolean;
}> = [
  {
    key: 'showDetailedAnalysis',
    label: 'Share detailed AI analysis',
    description: 'Per-leg breakdown, strategy, and market context',
    dependsOnRecentBets: false,
  },
  {
    key: 'allowTailParlays',
    label: 'Allow tailing my parlays',
    description: 'Others can copy your parlay legs to their builder',
    dependsOnRecentBets: true,
  },
  {
    key: 'showPropBreakdown',
    label: 'Show prop-type breakdown',
    description: 'Detailed stats by prop type (points, rebounds, etc.)',
    dependsOnRecentBets: false,
  },
  {
    key: 'showRecentBets',
    label: 'Show recent bet history',
    description: 'Full betting history with all legs',
    dependsOnRecentBets: false,
  },
  {
    key: 'showActivityFeed',
    label: 'Appear in activity feeds',
    description: "Show betting activity in others' feeds (requires recent bets)",
    dependsOnRecentBets: true,
  },
  {
    key: 'showFinancialDetails',
    label: 'Show financial details',
    description: 'Stakes, payouts, and profit/loss amounts',
    dependsOnRecentBets: false,
  },
];

/**
 * Filter permissions to only include those allowed for the tier
 * Used when sanitizing permissions on tier downgrade
 */
export function sanitizePermissions(
  permissions: Partial<Record<FollowerPermissionKey, boolean>>,
  tier: SubscriptionTier,
): Partial<Record<FollowerPermissionKey, boolean>> {
  const allowedPerms = TIER_PERMISSIONS[tier];
  if (!allowedPerms) {
    return {}; // Invalid tier, return empty (no permissions)
  }

  const sanitized: Partial<Record<FollowerPermissionKey, boolean>> = {};

  for (const [key, value] of Object.entries(permissions)) {
    if (allowedPerms.has(key as FollowerPermissionKey)) {
      sanitized[key as FollowerPermissionKey] = value;
    }
    // If not allowed, omit from result (effectively disabling it)
  }

  return sanitized;
}
